/**
 * The speakers of a transcript nobody has put a name on yet (#11884): the
 * labels the diarization layer made ("Them (A)", "Speaker B", a bare "Us"),
 * never a person's name. Mirrors the backend's placeholder rule
 * (`is_placeholder_label` in fp/processing/notetaker_speakers.py).
 */
const PLACEHOLDER = /^(?:(?:Us|Them)(?: \([A-Z]+\))?|Speaker(?: [A-Z]+)?)$/;

function isPlaceholderSpeaker(label: string): boolean {
  return PLACEHOLDER.test(label);
}

export interface UnidentifiedSpeaker {
  label: string;
  /** Lines the speaker has in the transcript. */
  lines: number;
  /** Their longest line, cut to a readable length: what a reader recognizes
   *  a person by. */
  sample: string;
}

const SAMPLE_CHARS = 140;

/** Unnamed speakers in order of first appearance. */
export function unidentifiedSpeakers(
  lines: readonly { speaker: string; text: string }[]
): UnidentifiedSpeaker[] {
  const byLabel = new Map<string, { lines: number; sample: string }>();
  for (const line of lines) {
    if (!line.speaker || !isPlaceholderSpeaker(line.speaker)) continue;
    const text = line.text.trim();
    const seen = byLabel.get(line.speaker);
    if (!seen) {
      byLabel.set(line.speaker, { lines: 1, sample: text });
    } else {
      seen.lines += 1;
      if (text.length > seen.sample.length) seen.sample = text;
    }
  }
  return [...byLabel.entries()].map(([label, { lines, sample }]) => ({
    label,
    lines,
    sample:
      sample.length > SAMPLE_CHARS
        ? `${sample.slice(0, SAMPLE_CHARS).trimEnd()}…`
        : sample,
  }));
}
